import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './GameStyles.css';

// Утилита для touch на Android WebView
const useTouchClick = (handler: () => void, disabled?: boolean) => {
  const touchedRef = useRef(false);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    touchedRef.current = true;
    handler();
    setTimeout(() => { touchedRef.current = false; }, 300);
  }, [disabled, handler]);
  const onClick = useCallback(() => {
    if (disabled || touchedRef.current) { touchedRef.current = false; return; }
    handler();
  }, [disabled, handler]);
  return { onTouchEnd, onClick };
};

interface NumberConfig {
  number: number;
  chance: number;
  boost_percentage: number;
  rarity: string;
}

interface LunarLotteryProps {
  onPlay: (choice?: any) => void;
  onClose: () => void;
  cost: number;
  maxReward: number;
  minReward?: number;
  error?: string;
  isPlaying?: boolean;
  config?: NumberConfig[];
  activeBoost?: {
    multiplier: number;
    timeLeft: number;
    expiresAt: string;
  } | null;
}

const LunarLottery: React.FC<LunarLotteryProps> = ({
  onPlay,
  onClose,
  cost,
  maxReward,
  error,
  isPlaying,
  config,
  activeBoost
}) => {
  const { t } = useTranslation();
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [showHelp, setShowHelp] = useState(false);

  // Дефолтный конфиг с разными редкостями
  const defaultConfig: NumberConfig[] = [
    { number: 1, chance: 0.12, boost_percentage: 50, rarity: 'common' },
    { number: 2, chance: 0.10, boost_percentage: 100, rarity: 'normal' },
    { number: 3, chance: 0.08, boost_percentage: 150, rarity: 'rare' },
    { number: 4, chance: 0.12, boost_percentage: 50, rarity: 'common' },
    { number: 5, chance: 0.10, boost_percentage: 100, rarity: 'normal' },
    { number: 6, chance: 0.08, boost_percentage: 150, rarity: 'rare' },
    { number: 7, chance: 0.12, boost_percentage: 50, rarity: 'common' },
    { number: 8, chance: 0.10, boost_percentage: 100, rarity: 'normal' },
    { number: 9, chance: 0.08, boost_percentage: 150, rarity: 'rare' },
    { number: 10, chance: 0.06, boost_percentage: 200, rarity: 'legendary' }
  ];

  const numbersConfig = config && config.length > 0 ? config : defaultConfig;

  // Цвета и стили для редкостей
  const rarityStyles: Record<string, { bg: string; border: string; label: string }> = {
    common: { bg: 'rgba(156, 163, 175, 0.2)', border: '#9ca3af', label: t('games.lunarLottery.rarity.common') },
    normal: { bg: 'rgba(59, 130, 246, 0.2)', border: '#3b82f6', label: t('games.lunarLottery.rarity.normal') },
    rare: { bg: 'rgba(168, 85, 247, 0.2)', border: '#a855f7', label: t('games.lunarLottery.rarity.rare') },
    legendary: { bg: 'rgba(245, 158, 11, 0.3)', border: '#f59e0b', label: t('games.lunarLottery.rarity.legendary') }
  };

  const getNumberConfig = (num: number) => numbersConfig.find(n => n.number === num) || numbersConfig[0];

  const toggleNumber = (num: number) => {
    if (selectedNumbers.includes(num)) {
      setSelectedNumbers(selectedNumbers.filter(n => n !== num));
    } else if (selectedNumbers.length < 3) {
      setSelectedNumbers([...selectedNumbers, num]);
    }
  };

  const handlePlay = useCallback(() => {
    if (selectedNumbers.length === 3) {
      console.log('🎮 LunarLottery play!', selectedNumbers);
      onPlay(selectedNumbers);
    }
  }, [selectedNumbers, onPlay]);

  const playBtnEvents = useTouchClick(handlePlay, selectedNumbers.length !== 3 || isPlaying);

  return (
    <div className="game-modal">
      <div className="game-content lunar-lottery">
        <div className="game-header">
          <h2>🌙 {t('games.lunarLottery.title')}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowHelp(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                color: 'white'
              }}
            >
              ❓
            </button>
            <button className="close-btn" onClick={onClose}>✖</button>
          </div>
        </div>

        {/* Легенда редкости */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '8px',
          fontSize: '9px'
        }}>
          <span style={{ color: '#9ca3af' }}>{t('games.lunarLottery.rarity.common')}</span>
          <span style={{ color: '#3b82f6' }}>{t('games.lunarLottery.rarity.normal')}</span>
          <span style={{ color: '#a855f7' }}>{t('games.lunarLottery.rarity.rare')}</span>
          <span style={{ color: '#f59e0b' }}>{t('games.lunarLottery.rarity.legend')}</span>
        </div>

        {/* Индикатор активного буста */}
        {activeBoost && activeBoost.timeLeft > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '8px',
            padding: '10px 14px',
            margin: '10px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
          }}>
            <span style={{ fontSize: '20px' }}>⚡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                {t('games.lunarLottery.activeBoost')} x{activeBoost.multiplier}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}>
                {t('games.lunarLottery.remaining')}: {Math.floor(activeBoost.timeLeft / 60)}:{(activeBoost.timeLeft % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        )}

        {/* Отображение ошибки */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            margin: '10px 0',
            color: '#ef4444',
            textAlign: 'center'
          }}>
            ❌ {error}
          </div>
        )}

        <div className="lottery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
          {[...Array(10)].map((_, i) => {
            const num = i + 1;
            const numConfig = getNumberConfig(num);
            const style = rarityStyles[numConfig.rarity] || rarityStyles.common;
            return (
              <button
                key={num}
                className={`lottery-number ${selectedNumbers.includes(num) ? 'selected' : ''}`}
                onClick={() => toggleNumber(num)}
                disabled={isPlaying}
                style={{
                  background: selectedNumbers.includes(num) ? style.border : style.bg,
                  borderColor: style.border,
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '8px 4px',
                  gap: '2px'
                }}
              >
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{num}</span>
                <span style={{ fontSize: '8px', opacity: 0.8 }}>{Math.round(numConfig.chance * 100)}%</span>
                <span style={{ fontSize: '7px', opacity: 0.7 }}>+{numConfig.boost_percentage}%</span>
              </button>
            );
          })}
        </div>

        {/* Выбранные числа с информацией */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          padding: '8px',
          margin: '8px 0',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '12px' }}>
            {t('games.lunarLottery.selected')}: {selectedNumbers.length > 0
              ? selectedNumbers.sort((a, b) => a - b).map(n => {
                  const cfg = getNumberConfig(n);
                  return `${n} (+${cfg.boost_percentage}%)`;
                }).join(', ')
              : t('games.lunarLottery.selectThree')}
          </p>
          {selectedNumbers.length === 3 && (
            <p style={{ margin: '4px 0 0', fontSize: '10px', color: '#10b981' }}>
              {t('games.lunarLottery.avgBoostOnWin')}: +{Math.round(selectedNumbers.reduce((sum, n) => sum + getNumberConfig(n).boost_percentage, 0) / 3)}%
            </p>
          )}
        </div>

        <button
          className="play-btn mobile-touch-btn"
          onTouchEnd={playBtnEvents.onTouchEnd}
          onClick={playBtnEvents.onClick}
          disabled={selectedNumbers.length !== 3 || isPlaying}
          style={{ touchAction: 'manipulation' }}
        >
          {isPlaying ? `⏳ ${t('games.lunarLottery.actions.playing')}` : (error ? `🔄 ${t('games.lunarLottery.actions.tryAgain')}` : `🎲 ${t('games.lunarLottery.actions.play')}`)}
        </button>

        {/* Модалка помощи */}
        {showHelp && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}
            onClick={() => setShowHelp(false)}
          >
            <div
              style={{
                background: 'linear-gradient(145deg, #1e1b4b 0%, #312e81 100%)',
                borderRadius: '16px',
                padding: '16px',
                maxWidth: '340px',
                width: '100%',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: 'white' }}>{t('games.lunarLottery.help.title')}</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ✖
                </button>
              </div>

              <div style={{ fontSize: '12px', lineHeight: '1.5', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>{t('games.lunarLottery.help.goal')}</strong><br />
                  {t('games.lunarLottery.help.goalText')}
                </p>

                <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
                  <p style={{ marginBottom: '6px', fontWeight: 'bold' }}>{t('games.lunarLottery.help.rarityTitle')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#9ca3af' }}>{t('games.lunarLottery.help.commonDesc')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#3b82f6' }}>{t('games.lunarLottery.help.normalDesc')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#a855f7' }}>{t('games.lunarLottery.help.rareDesc')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#f59e0b' }}>{t('games.lunarLottery.help.legendaryDesc')}</p>
                </div>

                <p style={{ marginBottom: '10px' }}>
                  <strong>{t('games.lunarLottery.help.strategy')}</strong><br />
                  {t('games.lunarLottery.help.strategyText1')}<br />
                  {t('games.lunarLottery.help.strategyText2')}
                </p>

                <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('games.lunarLottery.help.costLabel')} <strong>{t('games.lunarLottery.help.freeLabel')}</strong><br />
                  {t('games.lunarLottery.help.attempts')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LunarLottery;